/**
 * Transit results table with selection checkboxes, column headers, and overlap warnings.
 */
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  Box,
  Button,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { calculateTransitObservability } from '../utils/observabilityCalculations';
import TransitResultRow from './TransitResultRow';

const OverlapSummary = ({ selectedTransits, overlapMap }) => {
  let unselectedOverlapCount = 0;
  let selectedOverlapCount = 0;
  for (const [id, info] of overlapMap) {
    if (!selectedTransits.has(id) && info.overlapsWithSelected.length > 0) unselectedOverlapCount++;
    if (selectedTransits.has(id) && info.selectedOverlapsSelected.length > 0) selectedOverlapCount++;
  }
  if (unselectedOverlapCount === 0 && selectedOverlapCount === 0) return null;

  return (
    <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
      {unselectedOverlapCount > 0 && `${unselectedOverlapCount} transit${unselectedOverlapCount !== 1 ? 's' : ''} overlap with selection`}
      {unselectedOverlapCount > 0 && selectedOverlapCount > 0 && ' \u00b7 '}
      {selectedOverlapCount > 0 && `${selectedOverlapCount} selected transit${selectedOverlapCount !== 1 ? 's' : ''} overlap each other`}
    </Typography>
  );
};

const TransitResultsTable = ({
  transits,
  selectedTransits,
  handleSelectAll,
  handleSelectTransit,
  overlapMap,
  observatories,
  selectedObservatoryId,
  showOnlyFullTransits,
  baselineBefore,
  baselineAfter,
  filters,
  generatingPlan,
  onGeneratePlan,
  onShowDetails,
}) => {
  if (transits.length === 0) return null;

  const selectedObservatory = observatories.find(o => o._id === selectedObservatoryId);

  const filteredTransits = transits.filter(transit => {
    if (showOnlyFullTransits && selectedObservatoryId) {
      const observability = calculateTransitObservability(transit, selectedObservatory, {
        baselineBefore, baselineAfter, minAltitude: filters.minAltitude
      });
      return observability.canObserve === 'complete';
    }
    return true;
  });

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Found {transits.length} upcoming transit{transits.length !== 1 ? 's' : ''}
        </Typography>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedTransits.size === transits.length && transits.length > 0}
                indeterminate={selectedTransits.size > 0 && selectedTransits.size < transits.length}
                onChange={handleSelectAll}
              />
            }
            label="Select All"
          />
          <Button
            variant="contained"
            color="secondary"
            disabled={selectedTransits.size === 0 || generatingPlan}
            onClick={onGeneratePlan}
            startIcon={generatingPlan ? <CircularProgress size={20} /> : <ScheduleIcon />}
            sx={{ ml: 2 }}
          >
            Create Observations ({selectedTransits.size})
          </Button>
        </Box>
      </Box>

      {selectedTransits.size > 0 && overlapMap.size > 0 && (
        <OverlapSummary selectedTransits={selectedTransits} overlapMap={overlapMap} />
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedTransits.size === transits.length && transits.length > 0}
                  indeterminate={selectedTransits.size > 0 && selectedTransits.size < transits.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Observable</TableCell>
              <TableCell>Planet</TableCell>
              <TableCell>Host Star</TableCell>
              <TableCell align="center">V Mag</TableCell>
              <TableCell align="center">Obs Start</TableCell>
              <TableCell align="center">Transit Start</TableCell>
              <TableCell align="center">Transit End</TableCell>
              <TableCell align="center">Obs End</TableCell>
              <TableCell align="center">Duration</TableCell>
              <TableCell align="center">Depth (%)</TableCell>
              <TableCell align="center">Altitude</TableCell>
              <TableCell align="center">Moon Sep</TableCell>
              <TableCell align="center">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransits.map((transit, index) => {
              const transitId = `${transit.exoplanet.name}_${new Date(transit.transit.time_utc).getTime()}`;
              const isSelected = selectedTransits.has(transitId);
              const overlapInfo = overlapMap.get(transitId);
              const hasConflict = !isSelected && overlapInfo?.overlapsWithSelected?.length > 0;
              const hasSelectedConflict = isSelected && overlapInfo?.selectedOverlapsSelected?.length > 0;

              const observability = calculateTransitObservability(transit, selectedObservatory, {
                baselineBefore, baselineAfter, minAltitude: filters.minAltitude
              });

              return (
                <TransitResultRow
                  key={index}
                  transit={transit}
                  index={index}
                  isSelected={isSelected}
                  hasConflict={hasConflict}
                  hasSelectedConflict={hasSelectedConflict}
                  overlapInfo={overlapInfo}
                  observability={observability}
                  observatory={selectedObservatory}
                  baselineBefore={baselineBefore}
                  baselineAfter={baselineAfter}
                  onSelect={() => handleSelectTransit(transitId)}
                  onShowDetails={onShowDetails}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default TransitResultsTable;
